import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable, of } from "rxjs";
import { Barrier } from "@app/models/barrier";
import { environment } from "src/environments/environment";
import { PaginateList } from "@app/models/paginateList";
import { PaginateFilter } from "@app/models/filters/paginateFilter";
import { BarrierListFilter } from "@app/models/filters/barrierListFilter";
import { FileListFilter } from "@app/models/filters/fileListFilter";
import { File } from "@app/models/file";
import { AutocompleteItem } from "design-angular-kit";
import { ResultList } from "@app/models/resultList";

const baseUrl = environment.servicesDataUrl;
const baseUrlInter = environment.servicesInterUrl;

@Injectable({
    providedIn: "root",
})
export class BarrierService {
    private barrierListSubject = new BehaviorSubject<PaginateList<Barrier>>(new PaginateList());
    barrierList = this.barrierListSubject.asObservable();
    private barrierListFilterSubject = new BehaviorSubject<BarrierListFilter>(new BarrierListFilter());
    barrierListFilter = this.barrierListFilterSubject.asObservable();
    private imageListSubject = new BehaviorSubject<PaginateList<File>>(new PaginateList());
    imageList = this.imageListSubject.asObservable();

    constructor(private http: HttpClient) {}

    setBarrierList(barrierList: PaginateList<Barrier>): void {
        this.barrierListSubject.next(barrierList);
    }

    getBarrierList(): PaginateList<Barrier> {
        return this.barrierListSubject.getValue();
    }

    setBarrierListFilters(barrierFilterList: BarrierListFilter): void {
        this.barrierListFilterSubject.next(barrierFilterList);
    }

    getBarrierListFilters(): BarrierListFilter {
        return this.barrierListFilterSubject.getValue();
    }

    setImageList(imageList: PaginateList<File>): void {
        this.imageListSubject.next(imageList);
    }

    getImageList(): PaginateList<File> {
        return this.imageListSubject.getValue();
    }

    getAll(filters: PaginateFilter<BarrierListFilter>): Observable<PaginateList<Barrier>> {
        return this.http.post<PaginateList<Barrier>>(`${baseUrl}/sbarramenti`, filters);
    }

    getAllPublic(filters: PaginateFilter<BarrierListFilter>): Observable<PaginateList<Barrier>> {
        return this.http.post<PaginateList<Barrier>>(`${baseUrlInter}/public/sbarramenti`, filters);
    }

    search = (search?: string | null): Observable<Array<AutocompleteItem>> => {
        if (search) {
            const params = new HttpParams().set("denominazione", search);
            return this.http.get<ResultList<Barrier>>(`${baseUrl}/sbarramenti/bydenominazione`, { params }).pipe(
                map((res) =>
                    res.risultati.map((x: Barrier) => ({
                        value: x.denominazione,
                        label: x.indirizzo,
                        additionalData: x.uid,
                    }))
                )
            );
        } else return of([]);
    };

    export(filters: PaginateFilter<BarrierListFilter>, type: string): Observable<HttpResponse<Blob>> {
        const params = new HttpParams().set("tipo", type);
        return this.http.post(`${baseUrl}/sbarramenti/esporta`, filters, {
            params,
            responseType: "blob",
            observe: "response",
        });
    }

    get(id: string): Observable<Barrier> {
        return this.http.get<Barrier>(`${baseUrl}/sbarramento/${id}`);
    }

    getImage(id: number): Observable<Blob> {
        return this.http.get(`${baseUrl}/sbarramento/allegato/${id}`, { responseType: "blob" });
    }

    getImages(id: string | number, filters?: PaginateFilter<FileListFilter>): Observable<PaginateList<File>> {
        return this.http.post<PaginateList<File>>(`${baseUrl}/sbarramento/${id}/allegati`, filters);
    }

    create(data: Barrier): Observable<any> {
        return this.http.post(`${baseUrl}/sbarramento`, data);
    }

    update(data: Barrier): Observable<any> {
        return this.http.put(`${baseUrl}/sbarramento/${data.uid}`, data);
    }

    delete(id: number): Observable<any> {
        return this.http.delete(`${baseUrl}/sbarramento/${id}`);
    }

    uploadImage(id: string | number, data: File): Observable<any> {
        const attach = new FormData();
        attach.append("descrizione", data.descrizione);
        attach.append("file", data.file);
        return this.http.post(`${baseUrl}/sbarramento/${id}/allegato`, attach);
    }

    deleteImage(id: number): Observable<any> {
        return this.http.delete(`${baseUrl}/sbarramento/allegato/${id}`);
    }

    createVersion(id: string | number, data?: Barrier, aggiornaFascicolo = false): Observable<any> {
        const params = aggiornaFascicolo
            ? new HttpParams().set("aggiornaFascicolo", aggiornaFascicolo.toString())
            : new HttpParams();
        return this.http.put(`${baseUrl}/sbarramento/${id}/nuovaversione`, data, { params });
    }
}
